/**
 * `RimText` — the pressing-plant print around the rim of the dubplate.
 * `docs/design-direction.md` §6.4, §7.2 ROTATION.
 *
 * An SVG `<textPath>` around a circle, JetBrains Mono 700 at 9px with +0.16em
 * tracking in `--bg-paper-2`, reading `SOUND SYSTEM · DUBPLATE SPECIAL · 45 RPM ·`.
 * It counter-rotates against the record at 24s so the print stays roughly readable
 * while the disc turns; `styles/motion.css` owns that rotation and gates it on the
 * disc's `data-playing`, and the same `animation-play-state` freeze applies here —
 * the rim stops where it stopped.
 *
 * Three decisions worth recording:
 *
 *  · **The 9px is not a scale violation.** §4.3's closed set of nine governs *type*;
 *    this is rim print pressed into a record, and `styles/texture.css` records the
 *    exemption in the same words. It is set as an SVG attribute in *user units* of a
 *    320-unit viewBox — the disc's own maximum diameter — so the print scales with
 *    the record on a 320px phone instead of growing relative to it.
 *  · **`textLength` fills the ring.** The copy is repeated until it is a little
 *    longer than the circumference and then spacing-adjusted to exactly the
 *    circumference, so the ring closes with no gap and no overlap whatever font
 *    actually loaded — including the metric-matched fallback of §4.4.
 *  · **It is `aria-hidden`.** English label print carries no information a Russian
 *    screen-reader user needs (§11), and the track's real name is set beneath it.
 *
 * This is the one thing in the app that is allowed to be centred, because it is
 * circular (§12.5).
 */

import type { CSSProperties, JSX } from 'react';

import styles from './components.module.css';
import { cx } from './primitives/Rule';

/** The rim copy. Real label print, never invented patois (§6.8, §11). */
export const RIM_TEXT = 'SOUND SYSTEM · DUBPLATE SPECIAL · 45 RPM · ';

/** The disc's own maximum diameter, in user units, so 1 unit reads as 1px at 320px. */
const BOX = 320;
/** Radius of the text baseline: just inside the outer groove. */
const R = 140;
const CIRCUMFERENCE = Math.round(2 * Math.PI * R);
/** §6.4: JetBrains Mono 700, 9px. */
const FONT_SIZE = 9;
/** A mono glyph at 9px with +0.16em tracking advances ≈ 6.9 units. */
const ADVANCE = FONT_SIZE * 0.6 + FONT_SIZE * 0.16;

/** The `d` of a full circle, drawn as two half-arcs so every renderer agrees. */
const RING_PATH = `M${(BOX - 2 * R) / 2},${BOX / 2}a${R},${R} 0 1,1 ${2 * R},0a${R},${R} 0 1,1 ${-2 * R},0`;

/** Repeat the copy until it over-fills the ring; `textLength` takes up the slack. */
function fillRing(text: string): string {
  const chars = Math.ceil(CIRCUMFERENCE / ADVANCE);
  const repeats = Math.max(1, Math.ceil(chars / Math.max(1, text.length)));
  return text.repeat(repeats);
}

export interface RimTextProps {
  /** Mirrors playback, so the rim freezes with the record rather than resetting. */
  readonly playing?: boolean;
  /** Override only to print a different pressing's rim. Defaults to {@link RIM_TEXT}. */
  readonly text?: string;
  readonly className?: string | undefined;
}

export function RimText({
  playing = false,
  text = RIM_TEXT,
  className,
}: RimTextProps): JSX.Element {
  const pathId = 'blRimPath';
  const copy = fillRing(text);
  const style: CSSProperties = { animationPlayState: playing ? 'running' : 'paused' };

  return (
    <svg
      className={cx('disc__rim', className)}
      viewBox={`0 0 ${BOX} ${BOX}`}
      style={style}
      data-playing={playing ? 'true' : 'false'}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <path id={pathId} d={RING_PATH} fill="none" />
      </defs>
      <text
        className={cx('t-mono', styles.rim)}
        fontSize={FONT_SIZE}
        textLength={CIRCUMFERENCE}
        lengthAdjust="spacing"
      >
        <textPath href={`#${pathId}`}>{copy}</textPath>
      </text>
    </svg>
  );
}
